import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import '@/styles/ipe/workbench/modals/IpeDeleteConfirmModal.css';

const TITLE_ID = 'funding-delete-confirm-title';

function renderDeleteMessage(children) {
  if (typeof children !== 'string') {
    return <div className="ipe-delete-confirm__message">{children}</div>;
  }
  const parts = children.split(/(「[^」]+」)/g);
  return (
    <p className="ipe-delete-confirm__message">
      {parts.map((part, index) => (
        part.startsWith('「') ? (
          <strong key={index} className="ipe-delete-confirm__highlight">{part}</strong>
        ) : (
          <span key={index}>{part}</span>
        )
      ))}
    </p>
  );
}

/**
 * 统一删除确认弹窗
 */
export default function IpeDeleteConfirmModal({
  open,
  title,
  titleId = TITLE_ID,
  children,
  confirmLabel,
  cancelLabel,
  confirming = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  closeOnBackdrop = true,
  top = true,
}) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    // 推迟挂载键盘，避免操作列按钮的 Enter/点击同帧冒泡到「确认删除」
    let onKeyDown = null;
    const armTimer = window.setTimeout(() => {
      onKeyDown = (event) => {
        if (event.key === 'Escape') {
          onCancel?.();
        } else if (event.key === 'Enter' && !confirmDisabled && !confirming) {
          onConfirm?.();
        }
      };
      window.addEventListener('keydown', onKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(armTimer);
      if (onKeyDown) {
        window.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [open, onCancel, onConfirm, confirmDisabled, confirming]);

  if (!open) return null;

  return createPortal(
    <div className={`funding-modal ipe-delete-confirm${top ? ' funding-modal--top' : ''}`}>
      <div
        className="ipe-delete-confirm__backdrop"
        role="presentation"
        onClick={closeOnBackdrop && !confirming ? onCancel : undefined}
      />
      <div
        className="ipe-delete-confirm__panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ipe-delete-confirm__hero">
          <span className="ipe-delete-confirm__icon" aria-hidden="true">
            <AlertTriangle size={22} strokeWidth={2.2} />
          </span>
          <div className="ipe-delete-confirm__copy">
            <header className="ipe-delete-confirm__header">
              <h3 id={titleId}>{title || t('commonActions.deleteTitle')}</h3>
            </header>
            <div className="ipe-delete-confirm__body">
              {renderDeleteMessage(children)}
            </div>
          </div>
        </div>
        <footer className="ipe-delete-confirm__footer">
          <button
            type="button"
            className="ipe-delete-confirm__btn ipe-delete-confirm__btn--cancel"
            onClick={onCancel}
            disabled={confirming}
          >
            {cancelLabel || t('commonActions.cancel')}
          </button>
          <button
            type="button"
            className="ipe-delete-confirm__btn ipe-delete-confirm__btn--confirm"
            onClick={onConfirm}
            disabled={confirmDisabled || confirming}
          >
            {confirming ? t('commonActions.deleting') : (confirmLabel || t('commonActions.confirmDelete'))}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
